import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NoMeetingRoomIcon from '@mui/icons-material/NoMeetingRoom';

import {
  updateLead,
  deleteDropRequest,
} from '../../../../services/api/lead/lead';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEAL_STAGE,
  DealStage,
  DROPPED_MOTIVATION_IDS,
  translateDealStage,
} from '@tassomutuo-backoffice/types';
import useDateFormat from '../../../../hooks/useDateFormat';
import formatMoney from '../../../../utils/formatMoney';
// import { needUpdateNotify } from '../../../services/api/lead/lead';
// import { getUserRoleInLocalStorage } from '../../../services/localStorage/localStorage';
import { logMixPanelEvent } from '../../../../services/tracking/mixPanel';
import {
  EMPTY_MANAGED_LEAD_VALUE,
  formatManagedLeadDate,
  formatManagedLeadValue,
} from '../../../managedLead/managedLeadFormatting';
import DropRequestDatum from './DropRequestDatum';

export interface LeadDropProps {
  lead: {
    id: string;
    lead: {
      dealId: string;
      dealName: string;
      dealStage: DealStage;
      loanAmount: string;
      propertyValue: string;
      note: string;
      droppedMotivation: string;
      attoDate: Date | null;
      commission: string;
      appointmentDate: Date | null;
      appointmentTime: string | null;
      appointmentLocation: string;
      meeting_done?: string;
      confirmDrop?: boolean;
    };
    currentDealStage?: string;
    properties: {
      why_dropped: string | undefined;
      sales_manager_user: {
        email: string;
      };
    };
    reason: string;
    createdAt: Date;
    /** Read from HubSpot by the queue; null when HubSpot did not return it. */
    deal?: {
      createdAt: string | null;
      ownerEmail: string | null;
    } | null;
  };
}

const LeadCardList = ({ lead }: LeadDropProps) => {
  // const { renderActionRequired } = useLeadActionRequired();
  const { formatDate } = useDateFormat();
  const [doneInHubspot, setDoneInHubspot] = useState(false);

  const queryClient = useQueryClient();

  // const renderAction = renderActionRequired({ lead.lead });

  const id = lead.lead.dealId;

  const updateLeadMutation = useMutation({
    mutationFn: updateLead,
    onSuccess: (data, variables, context) => {
      logMixPanelEvent('confirm drop', { lead_id: id });
      logMixPanelEvent('lead update', { lead_id: id });
      queryClient.invalidateQueries({
        queryKey: ['leadDetails', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['leadsUnderManagement'],
      });

      queryClient.invalidateQueries({
        queryKey: ['leadNotes', id],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const deleteRequest = (leadDbId: string) => {
    deleteDropRequest(leadDbId)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['getDropRequests'],
        });
        toast.success('Richiesta di drop gestita con successo');
      })
      .catch((err) => {
        toast.error('Oooops... qualcosa è andato storto.');
      });
  };

  const confirmDrop = async ({
    dealStage,
    loanAmount,
    propertyValue,
    note,
    droppedMotivation,
    attoDate,
    commission,
    appointmentDate,
    appointmentTime,
    appointmentLocation,
    meeting_done,
    confirmDrop = true,
  }: {
    dealStage: DealStage;
    loanAmount: string;
    propertyValue: string;
    note: string;
    droppedMotivation: string;
    attoDate: Date | null;
    commission: string;
    appointmentDate: Date | null;
    appointmentTime: string | null;
    appointmentLocation: string;
    meeting_done?: string;
    confirmDrop?: boolean;
  }) => {
    await updateLeadMutation
      .mutateAsync({
        actionType: 'updateStatus',
        leadId: id!,
        dealName: leadProperties.dealName || 'Sconosciuta',
        dealStage,
        currentDealStage: leadProperties.dealStage || undefined,
        loanAmount,
        propertyValue,
        note,
        droppedMotivation,
        attoDateTimestamp: attoDate
          ? Date.UTC(
              +formatDate(attoDate, 'yyyy'),
              attoDate.getMonth(),
              +formatDate(attoDate, 'dd')
            )
          : undefined,
        commission,
        why_dropped:
          DROPPED_MOTIVATION_IDS.find((mot) => mot.label === lead.reason)
            ?.value || lead?.properties?.why_dropped,
        appointmentTimestamp: appointmentDate
          ? appointmentDate.getTime()
          : undefined,
        appointmentTime: appointmentTime || undefined,
        appointmentLocation,
        meeting_done,
        salesManagerEmail: lead?.properties?.sales_manager_user?.email,
        confirmDrop,
      })
      .then(() => {
        if (dealStage === DEAL_STAGE.DROPPED) {
          queryClient.invalidateQueries({
            queryKey: ['leadsDropped'],
          });
        }
        if (dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
          queryClient.invalidateQueries({
            queryKey: ['leadsClosed'],
          });
        }

        queryClient.invalidateQueries({
          queryKey: ['getDropRequests'],
        });

        const successMessage =
          dealStage === DEAL_STAGE.DROPPED
            ? 'La richiesta di drop è stata confermata'
            : 'Lead modificata con successo';
        toast.success(successMessage);
      });
  };

  // const addNoteHandler = async ({
  //   dealStage,
  //   note,
  // }: {
  //   dealStage: DealStage | undefined;
  //   note: string;
  // }) => {
  //   await updateLeadMutation.mutateAsync({
  //     actionType: 'updateStatus',
  //     leadId: id!,
  //     dealName: lead?.properties.dealname || 'Sconosciuta',
  //     dealStage,
  //     note
  //   });

  //   if (dealStage === DEAL_STAGE.DROPPED) {
  //     queryClient.invalidateQueries({
  //       queryKey: ['leadsDropped'],
  //     });
  //   }
  //   if (dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
  //     queryClient.invalidateQueries({
  //       queryKey: ['leadsClosed'],
  //     });
  //   }

  //   const successMessage =
  //     dealStage === DEAL_STAGE.DROPPED
  //       ? 'La tua richiesta di cambio stato a DROPPED è stata inviata'
  //       : 'Lead modificata con successo';
  //   toast.success(successMessage);
  // };

  const leadProperties = lead.lead;
  const meetingDone = leadProperties.meeting_done === 'true';

  return (
    <Card component="article" variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography component="h3" variant="h6">
              {leadProperties.dealName || EMPTY_MANAGED_LEAD_VALUE}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translateDealStage(lead.currentDealStage).label}
            </Typography>
          </Box>

          <Box
            component="dl"
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
            gap={2}
            m={0}
          >
            <DropRequestDatum
              label="Motivo"
              value={lead.reason || EMPTY_MANAGED_LEAD_VALUE}
            />
            <DropRequestDatum
              label="Data richiesta"
              value={
                lead.createdAt
                  ? formatDate(lead.createdAt, 'dd/MM/yyyy HH:mm')
                  : EMPTY_MANAGED_LEAD_VALUE
              }
            />
            <DropRequestDatum
              label="Data creazione lead"
              value={formatManagedLeadDate(lead.deal?.createdAt, formatDate)}
            />
            <DropRequestDatum
              label="Proprietario lead"
              value={formatManagedLeadValue(lead.deal?.ownerEmail)}
            />
            <DropRequestDatum
              label="Valore immobile"
              value={
                leadProperties.propertyValue
                  ? formatMoney(+leadProperties.propertyValue)
                  : EMPTY_MANAGED_LEAD_VALUE
              }
            />
            <DropRequestDatum
              label="Importo mutuo"
              value={
                leadProperties.loanAmount
                  ? formatMoney(+leadProperties.loanAmount)
                  : EMPTY_MANAGED_LEAD_VALUE
              }
            />
            <DropRequestDatum
              label="Appuntamento"
              value={
                <Stack direction="row" spacing={1} alignItems="center">
                  {meetingDone ? (
                    <MeetingRoomIcon color="success" fontSize="small" />
                  ) : (
                    <NoMeetingRoomIcon color="action" fontSize="small" />
                  )}
                  <span>{meetingDone ? 'Fatto' : 'Non fatto'}</span>
                </Stack>
              }
            />
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Nota
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            >
              {leadProperties.note || EMPTY_MANAGED_LEAD_VALUE}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            {leadProperties.dealStage !== DEAL_STAGE.ATTO_NOTARILE_CONCLUSO && (
              <Button
                variant="contained"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
                onClick={() =>
                  confirmDrop({ ...leadProperties, confirmDrop: true })
                }
              >
                Conferma il Drop
              </Button>
            )}

            {doneInHubspot && (
              <Button
                color="warning"
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
                onClick={() => deleteRequest(lead.id)}
              >
                Gestita in HubSpot
              </Button>
            )}

            <Button
              component={Link}
              to={`https://app-eu1.hubspot.com/contacts/139818512/record/0-3/${leadProperties.dealId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              onClick={() => setDoneInHubspot(true)}
            >
              Gestisci in HubSpot
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LeadCardList;
